/**
 * THE CHART'S TWO WRITTEN ROLES, AUTHORED AS PIXELS.
 *
 * CONTRACT 1.5e authors at 960x540 and delivers at 1920x1080 by integer 2. A web font
 * rasterised at 960x540 would be anti-aliased before the doubling, so every letter would
 * arrive at the player as soft two-pixel blocks on the ONE surface in this game that is
 * read rather than looked at. So the face is authored here, on the authoring grid, as
 * one-bit rows. Nothing in this file blends: a pixel is ink or it is not.
 *
 * TWO ROLES, AND NO MORE (the plan's limit):
 *
 *   HAND   5x7 cap, x-height 5, two descender rows. Proportional - each glyph is trimmed
 *          to its own ink and given a one-pixel sidebearing - because a fixed grid at this
 *          size reads as a terminal, and this is meant to read as a hand that wrote on a
 *          chart. Carries the phrase and the ledger's species names.
 *   TALLY  4x7 figures, fixed width, with a base serif. Carries counts, and only counts.
 *          Fixed width is the point: a column of figures in a ledger has to align, and
 *          the serif is what makes them read as tallied rather than as data.
 *
 * The two are deliberately different shapes at the same cap height, so a count never reads
 * as a word and a word never reads as a count. That is the whole hierarchy; there is no
 * third size, no bold, and no letterspaced all-caps.
 */

/** Rows 0..6 sit on the baseline; rows 7..8 are the descender. */
export const GLYPH_ROWS = 9;
export const BASELINE_ROW = 6;
export const CAP_H = 7;
/** Distance from one baseline to the next when the chart sets a list. */
export const LINE_H = 11;
/** Pixels between glyphs. One, always - the face is authored to need no kerning. */
export const LETTER_SPACING = 1;

export interface Glyph {
  readonly w: number;
  /** One entry per row 0..8; bit i (from the left) set means ink at column i. */
  readonly rows: readonly number[];
}

/** '#' is ink, anything else is not. Rows shorter than GLYPH_ROWS are padded empty. */
function glyph(art: readonly string[]): Glyph {
  const w = art.reduce((m, r) => Math.max(m, r.length), 0);
  const rows: number[] = [];
  for (let y = 0; y < GLYPH_ROWS; y++) {
    const line = art[y] ?? '';
    let bits = 0;
    for (let x = 0; x < w; x++) if (line[x] === '#') bits |= 1 << x;
    rows.push(bits);
  }
  // Trim empty columns from both sides so the face is proportional rather than gridded.
  let lo = 0;
  let hi = w - 1;
  const col = (x: number): boolean => rows.some((r) => (r & (1 << x)) !== 0);
  while (lo <= hi && !col(lo)) lo++;
  while (hi >= lo && !col(hi)) hi--;
  if (hi < lo) return { w: 0, rows: rows.map(() => 0) };
  return { w: hi - lo + 1, rows: rows.map((r) => r >>> lo) };
}

const HAND_ART: Readonly<Record<string, readonly string[]>> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.###.', '#...#', '#....', '#..##', '#...#', '#...#', '.###.'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'],
  J: ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  M: ['#...#', '##.##', '#.#.#', '#...#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '#.#.#', '#..##', '#...#', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#...#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],

  a: ['.....', '.....', '.###.', '....#', '.####', '#...#', '.####'],
  b: ['#....', '#....', '####.', '#...#', '#...#', '#...#', '####.'],
  c: ['.....', '.....', '.###.', '#....', '#....', '#....', '.###.'],
  d: ['....#', '....#', '.####', '#...#', '#...#', '#...#', '.####'],
  e: ['.....', '.....', '.###.', '#...#', '#####', '#....', '.###.'],
  f: ['..##.', '.#...', '####.', '.#...', '.#...', '.#...', '.#...'],
  g: ['.....', '.....', '.####', '#...#', '#...#', '#...#', '.####', '....#', '####.'],
  h: ['#....', '#....', '####.', '#...#', '#...#', '#...#', '#...#'],
  i: ['.#.', '...', '.#.', '.#.', '.#.', '.#.', '.#.'],
  j: ['..#', '...', '..#', '..#', '..#', '..#', '..#', '..#', '##.'],
  k: ['#....', '#....', '#..#.', '#.#..', '##...', '#.#..', '#..#.'],
  l: ['##.', '.#.', '.#.', '.#.', '.#.', '.#.', '.##'],
  m: ['.....', '.....', '##.#.', '#.#.#', '#.#.#', '#.#.#', '#.#.#'],
  n: ['.....', '.....', '####.', '#...#', '#...#', '#...#', '#...#'],
  o: ['.....', '.....', '.###.', '#...#', '#...#', '#...#', '.###.'],
  p: ['.....', '.....', '####.', '#...#', '#...#', '#...#', '####.', '#....', '#....'],
  q: ['.....', '.....', '.####', '#...#', '#...#', '#...#', '.####', '....#', '....#'],
  r: ['.....', '.....', '#.##.', '##...', '#....', '#....', '#....'],
  s: ['.....', '.....', '.####', '#....', '.###.', '....#', '####.'],
  t: ['.#...', '.#...', '####.', '.#...', '.#...', '.#...', '..##.'],
  u: ['.....', '.....', '#...#', '#...#', '#...#', '#...#', '.####'],
  v: ['.....', '.....', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  w: ['.....', '.....', '#...#', '#...#', '#.#.#', '#.#.#', '.#.#.'],
  x: ['.....', '.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#'],
  y: ['.....', '.....', '#...#', '#...#', '#...#', '#...#', '.####', '....#', '####.'],
  z: ['.....', '.....', '#####', '...#.', '..#..', '.#...', '#####'],

  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],

  '.': ['.', '.', '.', '.', '.', '.', '#'],
  ',': ['.', '.', '.', '.', '.', '.', '.#', '#.'],
  "'": ['#', '#'],
  '-': ['...', '...', '...', '...', '###'],
  ':': ['.', '.', '.', '#', '.', '.', '#'],
  '/': ['..#', '..#', '.#.', '.#.', '.#.', '#..', '#..'],
  '(': ['.#', '#.', '#.', '#.', '#.', '#.', '.#'],
  ')': ['#.', '.#', '.#', '.#', '.#', '.#', '#.'],
  '!': ['#', '#', '#', '#', '#', '.', '#'],
  '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
};

const TALLY_ART: Readonly<Record<string, readonly string[]>> = {
  '0': ['.##.', '#..#', '#..#', '#..#', '#..#', '#..#', '.##.'],
  '1': ['.##.', '..#.', '..#.', '..#.', '..#.', '..#.', '####'],
  '2': ['.##.', '#..#', '...#', '..#.', '.#..', '#...', '####'],
  '3': ['###.', '...#', '...#', '.##.', '...#', '...#', '###.'],
  '4': ['..#.', '.##.', '#.#.', '####', '..#.', '..#.', '..#.'],
  '5': ['####', '#...', '###.', '...#', '...#', '#..#', '.##.'],
  '6': ['.##.', '#...', '#...', '###.', '#..#', '#..#', '.##.'],
  '7': ['####', '...#', '..#.', '..#.', '.#..', '.#..', '.#..'],
  '8': ['.##.', '#..#', '#..#', '.##.', '#..#', '#..#', '.##.'],
  '9': ['.##.', '#..#', '#..#', '.###', '...#', '...#', '.##.'],
  '-': ['....', '....', '....', '....', '###.'],
};

function compile(art: Readonly<Record<string, readonly string[]>>): ReadonlyMap<string, Glyph> {
  const out = new Map<string, Glyph>();
  for (const [ch, rows] of Object.entries(art)) out.set(ch, glyph(rows));
  return out;
}

export const HAND: ReadonlyMap<string, Glyph> = compile(HAND_ART);
/** Fixed width, so a column of counts aligns without a measuring pass. */
export const TALLY: ReadonlyMap<string, Glyph> = compile(TALLY_ART);
export const TALLY_W = 4;

/** A word space, in the hand. Three pixels reads as a space at a 5-pixel cap. */
const SPACE_W = 3;

export function handWidth(text: string): number {
  let w = 0;
  for (const ch of text) {
    if (ch === ' ') {
      w += SPACE_W + LETTER_SPACING;
      continue;
    }
    const g = HAND.get(ch);
    if (!g) continue;
    w += g.w + LETTER_SPACING;
  }
  return Math.max(0, w - LETTER_SPACING);
}

export function tallyWidth(text: string): number {
  return text.length === 0 ? 0 : text.length * (TALLY_W + LETTER_SPACING) - LETTER_SPACING;
}

/** The one-bit surface everything in this file and chart.ts paints onto. */
export interface Bitmap {
  readonly w: number;
  readonly h: number;
  /** One byte per pixel: an INDEX into the chart's palette, 0 meaning nothing drawn. */
  readonly px: Uint8Array;
}

export function bitmap(w: number, h: number): Bitmap {
  return { w, h, px: new Uint8Array(w * h) };
}

export function put(b: Bitmap, x: number, y: number, ink: number): void {
  if (x < 0 || y < 0 || x >= b.w || y >= b.h) return;
  b.px[y * b.w + x] = ink;
}

function blitGlyph(b: Bitmap, g: Glyph, x: number, baselineY: number, ink: number): void {
  const top = baselineY - BASELINE_ROW;
  for (let row = 0; row < GLYPH_ROWS; row++) {
    const bits = g.rows[row] ?? 0;
    if (bits === 0) continue;
    for (let col = 0; col < g.w; col++) {
      if ((bits & (1 << col)) !== 0) put(b, x + col, top + row, ink);
    }
  }
}

/**
 * Set `text` in the hand, with its baseline on `baselineY`. Returns the pen's end x.
 *
 * A character the face does not carry is skipped rather than substituted, so a missing glyph
 * shows as a gap that gets noticed instead of a box that gets shipped. chart.test.ts sweeps
 * the whole vocabulary the game can say, so a gap is caught before a player sees it.
 *
 * `maxX` STOPS THE PEN AT A WHOLE GLYPH. The bitmap's own bounds are not enough: the chart
 * is a SHEET inset from the frame, so a phrase longer than the sheet would otherwise be
 * clipped by the frame and land its last letters on the open sea. Caught by the fence.
 */
export function drawHand(b: Bitmap, x: number, baselineY: number, text: string, ink: number, maxX = Infinity): number {
  let pen = x;
  for (const ch of text) {
    if (ch === ' ') {
      pen += SPACE_W + LETTER_SPACING;
      continue;
    }
    const g = HAND.get(ch);
    if (!g) continue;
    if (pen + g.w > maxX) break;
    blitGlyph(b, g, pen, baselineY, ink);
    pen += g.w + LETTER_SPACING;
  }
  return pen - LETTER_SPACING;
}

/** Set figures in the tally, fixed width. Returns the pen's end x. */
export function drawTally(b: Bitmap, x: number, baselineY: number, text: string, ink: number, maxX = Infinity): number {
  let pen = x;
  for (const ch of text) {
    if (pen + TALLY_W > maxX) break;
    const g = TALLY.get(ch);
    if (g) blitGlyph(b, g, pen + Math.floor((TALLY_W - g.w) / 2), baselineY, ink);
    pen += TALLY_W + LETTER_SPACING;
  }
  return pen - LETTER_SPACING;
}
